import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Zap,
  Users,
  BarChart3,
  Shield,
  Loader2,
  Sparkles,
  TrendingUp,
  DollarSign,
  Clock,
  Building2,
  ChevronRight,
  Ticket,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const TenantSignup = () => {
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoDiscount, setPromoDiscount] = useState(null);
  const [checkingPromo, setCheckingPromo] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    owner_name: '',
    owner_email: '',
    owner_password: '',
    confirm_password: '',
  });

  const validatePromoCode = async (code) => {
    if (!code || code.length < 3) {
      setPromoDiscount(null);
      return;
    }
    
    setCheckingPromo(true);
    try {
      const response = await api.get(`/discount-codes/validate/${code}`);
      setPromoDiscount(response.data);
      toast.success(`Promo code applied: ${response.data.description}`);
    } catch (error) {
      setPromoDiscount(null);
      if (code.length > 3) {
        toast.error('Invalid promo code');
      }
    } finally {
      setCheckingPromo(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.owner_password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.owner_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await api.post('/organizations/signup', {
        name: formData.name,
        owner_name: formData.owner_name,
        owner_email: formData.owner_email,
        owner_password: formData.owner_password,
      });
      
      // Use loginWithToken to update AuthContext state immediately
      loginWithToken(response.data.token, response.data.user);
      
      toast.success('Welcome! Your 14-day trial has started.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Users, title: 'Lead Management', desc: 'Capture & track solar leads' },
    { icon: TrendingUp, title: 'Sales Pipeline', desc: '10-stage deal tracking' },
    { icon: DollarSign, title: 'Commission Tracking', desc: 'Automated payouts' },
    { icon: BarChart3, title: 'Reports & Analytics', desc: 'Real-time insights' },
    { icon: Shield, title: 'Team Management', desc: 'Roles & permissions' },
    { icon: Zap, title: 'Fast & Modern', desc: 'Built for speed' },
  ];

  // Animated counter for stats
  const [stats, setStats] = useState({ companies: 0, deals: 0, kw: 0 });
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        companies: prev.companies < 150 ? prev.companies + 3 : 150,
        deals: prev.deals < 12500 ? prev.deals + 250 : 12500,
        kw: prev.kw < 890 ? prev.kw + 18 : 890,
      }));
    }, 50);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-white text-slate-800">
      {/* Header */}
      <header className="py-6 px-8 bg-white/80 backdrop-blur-md border-b border-slate-200">
        <nav className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="/logo.png" 
              alt="Solar Moguls" 
              className="w-12 h-12 object-contain"
            />
            <span className="text-2xl font-bold text-slate-800">
              Solar Moguls
            </span>
          </Link>
          <Link to="/login">
            <Button variant="ghost" className="text-slate-600 hover:text-slate-800 hover:bg-slate-100">
              Sign In
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Side - Marketing */}
          <div className="space-y-8">
            <div className="space-y-4">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 px-4 py-1.5 text-sm font-medium">
                <Sparkles className="w-4 h-4 mr-2" />
                14-Day Free Trial
              </Badge>
              
              <h1 className="text-5xl lg:text-6xl font-bold leading-tight text-slate-900">
                Power Your Solar
                <br />
                <span className="text-blue-600">
                  Sales Empire
                </span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                The all-in-one CRM built for solar sales teams. 
                Track leads, manage commissions, and close more deals.
              </p>
            </div>

            {/* Pricing Card */}
            <Card className="bg-white border-slate-200 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-slate-500 text-sm font-medium">Monthly Subscription</p>
                    <div className="flex items-baseline gap-2 mt-1">
                      {promoDiscount ? (
                        <>
                          <span className="text-2xl font-bold text-slate-400 line-through">$349.99</span>
                          <span className="text-4xl font-bold text-blue-600">
                            {promoDiscount.discount_type === 'percentage' 
                              ? `$${(349.99 * (1 - promoDiscount.discount_value / 100)).toFixed(2)}`
                              : `$${Math.max(0, 349.99 - promoDiscount.discount_value).toFixed(2)}`
                            }
                          </span>
                          <span className="text-slate-500">/mo</span>
                        </>
                      ) : (
                        <>
                          <span className="text-4xl font-bold text-slate-900">$349</span>
                          <span className="text-slate-500">.99/mo</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center gap-2 text-blue-600">
                      <Clock className="w-5 h-5" />
                      <span className="font-semibold">14 Days Free</span>
                    </div>
                    <p className="text-slate-500 text-sm mt-1">No charge until trial ends</p>
                  </div>
                </div>
                
                {/* Promo Code Input */}
                <div className="pt-4 border-t border-slate-200">
                  <Label className="text-slate-600 text-sm flex items-center gap-2 font-medium">
                    <Ticket className="w-4 h-4" />
                    Have a promo code?
                  </Label>
                  <div className="flex gap-2 mt-2">
                    <Input
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      className="bg-white border-slate-300 text-slate-800 font-mono uppercase focus:border-blue-500 focus:ring-blue-500/20"
                      data-testid="promo-code-input"
                    />
                    <Button
                      variant="outline"
                      onClick={() => validatePromoCode(promoCode)}
                      disabled={checkingPromo || !promoCode}
                      className="border-slate-300 text-slate-700 hover:bg-slate-50"
                      data-testid="apply-promo-btn"
                    >
                      {checkingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                  {promoDiscount && (
                    <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-teal-600" />
                      <span className="text-teal-700 text-sm font-medium">
                        {promoDiscount.description}
                      </span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Features Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {features.map((feature, idx) => (
                <div 
                  key={feature.title}
                  className="group p-4 rounded-xl bg-white border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all duration-300"
                >
                  <feature.icon className="w-6 h-6 text-blue-600 mb-2 group-hover:scale-110 transition-transform" />
                  <h3 className="font-semibold text-slate-800 text-sm">{feature.title}</h3>
                  <p className="text-slate-500 text-xs mt-1">{feature.desc}</p>
                </div>
              ))}
            </div>
            
            {/* Stats Counter */}
            <div className="grid grid-cols-3 gap-4 pt-4">
              <div className="text-center p-4 rounded-xl bg-blue-50 border border-blue-100">
                <p className="text-3xl font-bold text-blue-600 font-mono">{stats.companies}+</p>
                <p className="text-slate-600 text-sm mt-1">Solar Companies</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-teal-50 border border-teal-100">
                <p className="text-3xl font-bold text-teal-600 font-mono">{stats.deals.toLocaleString()}+</p>
                <p className="text-slate-600 text-sm mt-1">Deals Tracked</p>
              </div>
              <div className="text-center p-4 rounded-xl bg-amber-50 border border-amber-100">
                <p className="text-3xl font-bold text-amber-600 font-mono">{stats.kw}MW</p>
                <p className="text-slate-600 text-sm mt-1">Solar Installed</p>
              </div>
            </div>
          </div>

          {/* Right Side - Signup Form */}
          <div className="relative">
            <Card className="bg-white border-slate-200 shadow-2xl">
              <CardContent className="p-8">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-slate-900">Start Your Free Trial</h2>
                  <p className="text-slate-500 mt-1">No credit card required</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Company Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)}
                      placeholder="Acme Solar Inc."
                      required
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 h-12"
                      data-testid="company-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Your Name</Label>
                    <Input
                      value={formData.owner_name}
                      onChange={(e) => handleChange('owner_name', e.target.value)}
                      placeholder="John Smith"
                      required
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 h-12"
                      data-testid="owner-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Email Address</Label>
                    <Input
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) => handleChange('owner_email', e.target.value)}
                      placeholder="john@acmesolar.com"
                      required
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 h-12"
                      data-testid="owner-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Password</Label>
                    <Input
                      type="password"
                      value={formData.owner_password}
                      onChange={(e) => handleChange('owner_password', e.target.value)}
                      placeholder="••••••••"
                      required
                      minLength={8}
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 h-12"
                      data-testid="owner-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium">Confirm Password</Label>
                    <Input
                      type="password"
                      value={formData.confirm_password}
                      onChange={(e) => handleChange('confirm_password', e.target.value)}
                      placeholder="••••••••"
                      required
                      className="bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:ring-blue-500/20 h-12"
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !formData.name || !formData.owner_name || !formData.owner_email || !formData.owner_password}
                    className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg"
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Creating Account...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Start Free Trial
                      </>
                    )}
                  </Button>
                </form>

                {/* Terms */}
                <p className="text-center text-slate-500 text-sm mt-6">
                  By signing up, you agree to our{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline">
                    Terms of Service
                  </Link>
                </p>
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <div className="flex items-center justify-center gap-6 mt-6 text-slate-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-teal-600" />
                <span className="text-sm">Secure & Encrypted</span>
              </div>
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-600" />
                <span className="text-sm">Your Data, Your Ownership</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TenantSignup;
